import { useCallback } from 'react';

import { useTheme } from '@mui/material';

import type { ChartAppearance } from '@/components/Chart/theme/chartAppearance';
import type {
  ExtendedOptions,
  ExtendedSeriesOptionsType
} from '@/components/Chart/types/highcharts';
import { useUserConfig } from '@/features/user-config';

import createRangeSelector from '../useRangeSelector';
import useChartFunctions from './useChartFunctions';

const DEFAULT_TIMEZONE = 'UTC';

const useBaseChartOptions = (height: number) => {
  const theme = useTheme();
  const { data: userConfigData } = useUserConfig();
  const timezone = userConfigData?.timezone.value ?? DEFAULT_TIMEZONE;
  const { createNavigator, createPlotOptions, createYAxis, createXAxis } = useChartFunctions();

  const createBaseChartOptions = useCallback(
    (chartSeries: ExtendedSeriesOptionsType[], appearance: ChartAppearance): ExtendedOptions => {
      const { textColor, backgroundColor, palette, fontFamily } = appearance;

      return {
        accessibility: { enabled: false },
        time: { timezone },
        title: {
          text: undefined
        },
        colors: palette,
        chart: {
          reflow: true,
          animation: false,
          type: 'line',
          backgroundColor,
          height,
          style: {
            fontFamily
          },
          zooming: {
            type: 'x'
          }
        },
        exporting: {
          scale: 1,
          enabled: true,
          filename: 'GX-Chart',
          chartOptions: {
            legend: {
              enabled: true,
              layout: 'horizontal',
              itemStyle: {
                color: textColor,
                fontSize: '10px'
              }
            },
            xAxis: {
              labels: {
                style: {
                  fontSize: '9px'
                }
              }
            },
            yAxis: {
              labels: {
                style: {
                  fontSize: '9px'
                }
              }
            },
            plotOptions: {
              series: {
                dataLabels: {
                  enabled: false
                }
              }
            },
            navigator: {
              enabled: false
            },
            rangeSelector: { enabled: false },
            scrollbar: {
              enabled: false
            }
          }
        },
        xAxis: createXAxis(textColor),
        yAxis: createYAxis(textColor),

        plotOptions: createPlotOptions(),
        series: chartSeries,
        legend: { enabled: false },
        navigator: createNavigator(theme),
        rangeSelector: createRangeSelector(theme, chartSeries),
        credits: {
          enabled: false
        }
      };
    },
    [createNavigator, createPlotOptions, createXAxis, createYAxis, height, theme, timezone]
  );

  return { createBaseChartOptions };
};

export default useBaseChartOptions;
